import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Download,
  FileText,
  MapPin,
  MoreHorizontal,
  Pause,
  Pencil,
  Play,
  Plus,
  Star,
  Trash2,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/admin/reports")({
  head: () => ({
    meta: [
      { title: "Advanced Reporting — Spayzo" },
      {
        name: "description",
        content: "Deep-dive analytics across bookings, users, merchants & revenue.",
      },
    ],
  }),
  component: AdvancedReporting,
});

type CategoryKey = "Workspace" | "Wellness" | "Social" | "Creative";

const CATEGORY_COLORS: Record<CategoryKey, string> = {
  Workspace: "#5958F3",
  Wellness: "#22C55E",
  Social: "#F59E0B",
  Creative: "#EC4899",
};

const bookingsTrend: Array<{ d: string } & Record<CategoryKey, number>> = [
  { d: "Jun 1", Workspace: 120, Wellness: 40, Social: 60, Creative: 90 },
  { d: "Jun 3", Workspace: 132, Wellness: 55, Social: 72, Creative: 102 },
  { d: "Jun 5", Workspace: 128, Wellness: 48, Social: 80, Creative: 110 },
  { d: "Jun 7", Workspace: 150, Wellness: 62, Social: 95, Creative: 125 },
  { d: "Jun 10", Workspace: 165, Wellness: 70, Social: 110, Creative: 140 },
  { d: "Jun 12", Workspace: 180, Wellness: 78, Social: 125, Creative: 155 },
  { d: "Jun 15", Workspace: 175, Wellness: 82, Social: 130, Creative: 162 },
  { d: "Jun 17", Workspace: 190, Wellness: 90, Social: 145, Creative: 178 },
  { d: "Jun 20", Workspace: 210, Wellness: 100, Social: 160, Creative: 195 },
  { d: "Jun 22", Workspace: 205, Wellness: 105, Social: 170, Creative: 210 },
  { d: "Jun 25", Workspace: 230, Wellness: 115, Social: 185, Creative: 225 },
  { d: "Jun 27", Workspace: 240, Wellness: 125, Social: 195, Creative: 240 },
  { d: "Jun 29", Workspace: 235, Wellness: 130, Social: 205, Creative: 255 },
];

const moodShare = [
  { key: "focus", value: 38, color: "#5958F3" },
  { key: "wellness", value: 23, color: "#22C55E" },
  { key: "social", value: 17, color: "#F59E0B" },
  { key: "creative", value: 12, color: "#EC4899" },
  { key: "other", value: 10, color: "#94A3B8" },
];

const acquisition = [
  { key: "organic", users: 18200, share: 38, color: "#5958F3" },
  { key: "socialMedia", users: 11400, share: 24, color: "#22C55E" },
  { key: "paid", users: 9700, share: 17, color: "#F59E0B" },
  { key: "direct", users: 8150, share: 11, color: "#EC4899" },
  { key: "referrals", users: 6200, share: 10, color: "#94A3B8" },
];

const PERIODS = ["7D", "30D", "3M", "6M", "YTD"] as const;
type PeriodKey = (typeof PERIODS)[number];

const kpis = [
  { key: "bookings", value: "5,847", delta: "+24.3%", icon: TrendingUp, tint: "#5958F3" },
  { key: "users", value: "3,847", delta: "+11.7%", icon: Users, tint: "#22C55E" },
  { key: "merchants", value: "12,490", delta: "+6", icon: MapPin, tint: "#F59E0B" },
  { key: "rating", value: "4.71", delta: "+0.12", icon: Star, tint: "#EC4899" },
];

const topSpaces = [
  { rank: "01", name: "The Focus Loft", category: "Workspace Hub", location: "DIFC", occupancy: 88, bookings: 286, revenue: <Money value="214,000" />, delta: "↑ 18%" },
  { rank: "02", name: "Serenity Room", category: "Studio Serene", location: "Marina", occupancy: 74, bookings: 192, revenue: <Money value="178,000" />, delta: "↑ 12%" },
  { rank: "03", name: "Zen Garden Suite", category: "Zen Collective", location: "JLT", occupancy: 69, bookings: 161, revenue: <Money value="74,000" />, delta: "↑ 9%" },
  { rank: "04", name: "Rooftop Terrace", category: "The Rooftop", location: "Downtown", occupancy: 65, bookings: 148, revenue: <Money value="66,000" />, delta: "↑ 5%" },
  { rank: "05", name: "Flow Studio", category: "Flow Lounge", location: "Marina", occupancy: 58, bookings: 135, revenue: <Money value="58,000" />, delta: "↓ 2%" },
];

type ScheduledReport = {
  id: string;
  name: string;
  type: string;
  frequency: string;
  recipients: string;
  lastRun: string;
  nextRun: string;
  status: "Active" | "Paused";
};

const initialReports: ScheduledReport[] = [
  { id: "r1", name: "Weekly Booking Report", type: "Operations", frequency: "Weekly", recipients: "ops@spayzo.com +2", lastRun: "Jun 28, 2025", nextRun: "Jul 5, 2025", status: "Active" },
  { id: "r2", name: "Monthly Revenue Summary", type: "Finance", frequency: "Monthly", recipients: "finance@spayzo.com +1", lastRun: "Jun 1, 2025", nextRun: "Jul 1, 2025", status: "Active" },
  { id: "r3", name: "Partner Performance Digest", type: "Partnerships", frequency: "Bi-weekly", recipients: "partners@spayzo.com +3", lastRun: "Jun 15, 2025", nextRun: "Jun 29, 2025", status: "Paused" },
];

function ChartTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string; dataKey: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border/60 bg-background/95 p-3 shadow-lg backdrop-blur">
      {label && <p className="mb-1.5 text-[11px] font-semibold text-muted-foreground">{label}</p>}
      <div className="space-y-1">
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-2 text-xs">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-muted-foreground">{p.name}</span>
            <span className="ms-auto font-semibold">{p.value.toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdvancedReporting() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<PeriodKey>("30D");
  const [region, setRegion] = useState("all-regions");
  const [category, setCategory] = useState<"all-categories" | Lowercase<CategoryKey>>(
    "all-categories",
  );
  const [reports, setReports] = useState<ScheduledReport[]>(initialReports);

  const visibleCategories = useMemo<CategoryKey[]>(() => {
    if (category === "all-categories") return ["Workspace", "Wellness", "Social", "Creative"];
    return [(category[0].toUpperCase() + category.slice(1)) as CategoryKey];
  }, [category]);

  const trendData = useMemo(() => {
    // Trim points based on selected period
    const slice =
      period === "7D" ? bookingsTrend.slice(-4) :
      period === "30D" ? bookingsTrend :
      period === "3M" ? bookingsTrend :
      bookingsTrend;
    return slice;
  }, [period]);

  const activeFilters =
    (region !== "all-regions" ? 1 : 0) +
    (category !== "all-categories" ? 1 : 0) +
    (period !== "30D" ? 1 : 0);

  const clearFilters = () => {
    setRegion("all-regions");
    setCategory("all-categories");
    setPeriod("30D");
  };

  const periodLabel = t(`adminReports.periodLabels.${period}`);

  const moodData = useMemo(
    () => moodShare.map((m) => ({ ...m, name: t(`adminReports.moods.${m.key}`) })),
    [t],
  );
  const acqData = useMemo(
    () => acquisition.map((a) => ({ ...a, channel: t(`adminReports.channels.${a.key}`) })),
    [t],
  );

  const toggleReport = (id: string) => {
    setReports((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, status: r.status === "Active" ? "Paused" : "Active" } : r,
      ),
    );
    const target = reports.find((r) => r.id === id);
    toast.success(
      target?.status === "Active"
        ? t("adminReports.toasts.paused")
        : t("adminReports.toasts.activated"),
    );
  };

  const deleteReport = (id: string) => {
    setReports((prev) => prev.filter((r) => r.id !== id));
    toast.success(t("adminReports.toasts.removed"));
  };

  const handleExport = () => toast.success(t("adminReports.toasts.exportQueued"));
  const handleGenerate = () => toast.success(t("adminReports.toasts.generating"));
  const handleNewSchedule = () => toast.success(t("adminReports.toasts.newSchedule"));

  return (
    <AdminShell
      title={t("pages.admin.reports.title")}
      subtitle={t("pages.admin.reports.subtitle")}
      actions={
        <>
          <Button
            variant="outline"
            className="h-10 gap-2 rounded-xl"
            onClick={handleExport}
          >
            <Download className="h-4 w-4" />
            {t("adminReports.export")}
          </Button>
          <Button
            className="h-10 gap-2 rounded-xl bg-[#5958F3] shadow-soft hover:bg-[#5958F3]/90"
            onClick={handleGenerate}
          >
            <FileText className="h-4 w-4" />
            {t("adminReports.generate")}
          </Button>
        </>
      }
    >
      {/* Toolbar */}
      <Card className="mb-6 rounded-2xl border-border/60 p-4 shadow-soft">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex h-10 items-center gap-2 rounded-xl border border-border/60 bg-background px-3 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{periodLabel}</span>
            </div>
            <Tabs value={period} onValueChange={(v) => setPeriod(v as PeriodKey)} className="w-auto">
              <TabsList className="h-10 rounded-xl bg-muted p-1">
                {PERIODS.map((p) => (
                  <TabsTrigger
                    key={p}
                    value={p}
                    className="rounded-lg px-3 text-xs font-medium"
                  >
                    {t(`adminReports.periods.${p}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="h-10 w-40 rounded-xl">
                <SelectValue placeholder={t("adminReports.allRegions")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-regions">{t("adminReports.allRegions")}</SelectItem>
                {["riyadh", "jeddah", "mecca", "medina", "dammam", "khobar"].map((r) => (
                  <SelectItem key={r} value={r}>
                    {t(`adminReports.regions.${r}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={category} onValueChange={(v) => setCategory(v as typeof category)}>
              <SelectTrigger className="h-10 w-40 rounded-xl">
                <SelectValue placeholder={t("adminReports.allCategories")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-categories">{t("adminReports.allCategories")}</SelectItem>
                {["workspace", "wellness", "social", "creative"].map((c) => (
                  <SelectItem key={c} value={c}>
                    {t(`adminReports.categories.${c}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activeFilters > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-10 gap-1.5 rounded-xl text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
                {t("adminReports.clear", { count: activeFilters })}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((k) => (
          <Card
            key={k.key}
            className="relative overflow-hidden rounded-2xl border-border/60 p-5 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <span
              className="absolute inset-x-0 top-0 h-1"
              style={{ backgroundColor: k.tint }}
            />
            <div className="flex items-center justify-between">
              <span
                className="grid h-10 w-10 place-items-center rounded-xl"
                style={{ backgroundColor: `${k.tint}1a`, color: k.tint }}
              >
                <k.icon className="h-5 w-5" />
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
                <ArrowUpRight className="h-3 w-3" />
                {k.delta}
              </span>
            </div>
            <p className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t(`adminReports.kpi.${k.key}`)}
            </p>
            <p className="mt-1 font-display text-2xl font-bold tracking-tight">{k.value}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t(`adminReports.kpi.${k.key}Hint`)}</p>
          </Card>
        ))}
      </div>

      {/* Charts row 1 */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="rounded-2xl border-border/60 p-5 shadow-soft lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{t("adminReports.trend.title")}</p>
              <p className="text-xs text-muted-foreground">
                {t("adminReports.trend.subtitle", { range: periodLabel })}
              </p>
            </div>
            <Badge variant="secondary" className="rounded-full">
              {t("adminReports.trend.daily")}
            </Badge>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  {(Object.keys(CATEGORY_COLORS) as CategoryKey[]).map((c) => (
                    <linearGradient key={c} id={`grad-${c}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={CATEGORY_COLORS[c]} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={CATEGORY_COLORS[c]} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="d" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
                <RTooltip content={<ChartTooltip />} />
                {visibleCategories.map((c) => (
                  <Area
                    key={c}
                    type="monotone"
                    dataKey={c}
                    stackId="1"
                    name={t(`adminReports.categories.${c.toLowerCase()}`)}
                    stroke={CATEGORY_COLORS[c]}
                    fill={`url(#grad-${c})`}
                    strokeWidth={2}
                  />
                ))}
                <Legend iconType="circle" wrapperStyle={{ paddingTop: 16, fontSize: 12 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
          <div className="mb-4">
            <p className="text-sm font-semibold">{t("adminReports.share.title")}</p>
            <p className="text-xs text-muted-foreground">{t("adminReports.share.subtitle")}</p>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={moodData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {moodShare.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RTooltip
                  formatter={(v: number, name: string) => [`${v}%`, name]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid hsl(var(--border))",
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {moodShare.map((m) => (
              <div key={m.key} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: m.color }} />
                  <span className="text-muted-foreground">{t(`adminReports.moods.${m.key}`)}</span>
                </div>
                <span className="font-semibold">{m.value}%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Acquisition + Revenue + Retention */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="rounded-2xl border-border/60 p-5 shadow-soft lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{t("adminReports.acq.title")}</p>
              <p className="text-xs text-muted-foreground">
                {t("adminReports.acq.subtitle", { range: periodLabel })}
              </p>
            </div>
            <div className="text-end">
              <p className="font-display text-2xl font-bold">+3,284</p>
              <p className="text-xs text-muted-foreground">{t("adminReports.acq.users")}</p>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={acqData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="channel" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={50} />
                <RTooltip
                  cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }}
                  formatter={(v: number) => [v.toLocaleString(), t("adminReports.acq.usersLabel")]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid hsl(var(--border))",
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="users" radius={[6, 6, 0, 0]}>
                  {acqData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {acquisition.map((a) => (
              <div
                key={a.key}
                className="rounded-xl border border-border/50 bg-surface p-3 transition-colors hover:border-[#5958F3]/40"
              >
                <div className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: a.color }} />
                  <p className="text-[10px] text-muted-foreground">
                    {t(`adminReports.channels.${a.key}`)}
                  </p>
                </div>
                <p className="mt-1 font-display text-sm font-semibold">{a.users.toLocaleString()}</p>
                <p className="text-[10px] font-medium text-emerald-600">{a.share}%</p>
              </div>
            ))}
          </div>
        </Card>

        <div className="flex flex-col gap-4">
          <Card className="flex-1 rounded-2xl border-border/60 p-5 shadow-soft">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">{t("adminReports.revenue.title")}</p>
                <p className="text-xs text-muted-foreground">{t("adminReports.revenue.subtitle")}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 rounded-lg text-[#5958F3] hover:bg-[#5958F3]/10 hover:text-[#5958F3]"
                onClick={() => toast.info(t("adminReports.revenue.opening"))}
              >
                {t("adminReports.revenue.viewAll")}
              </Button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-xl bg-surface p-3">
                <span className="text-xs text-muted-foreground">{t("adminReports.revenue.gross")}</span>
                <span className="font-semibold"><Riyal /> 284,920</span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-surface p-3">
                <span className="text-xs text-muted-foreground">{t("adminReports.revenue.commission")}</span>
                <span className="font-semibold"><Riyal /> 42,738</span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-surface p-3">
                <span className="text-xs text-muted-foreground">{t("adminReports.revenue.payouts")}</span>
                <span className="font-semibold"><Riyal /> 228,184</span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-emerald-50 p-3 dark:bg-emerald-500/10">
                <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                  {t("adminReports.revenue.net")}
                </span>
                <span className="font-semibold text-emerald-700 dark:text-emerald-400"><Riyal /> 56,738
                </span>
              </div>
            </div>
          </Card>

          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <p className="text-sm font-semibold">{t("adminReports.retention.title")}</p>
            <p className="text-xs text-muted-foreground">{t("adminReports.retention.subtitle")}</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-surface p-3">
                <p className="text-[10px] text-muted-foreground">{t("adminReports.retention.rate")}</p>
                <p className="font-display text-xl font-bold">72.4%</p>
                <p className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600">
                  <ArrowUpRight className="h-3 w-3" /> +3.1% {t("adminReports.retention.vsLastMonth")}
                </p>
              </div>
              <div className="rounded-xl bg-surface p-3">
                <p className="text-[10px] text-muted-foreground">{t("adminReports.retention.churn")}</p>
                <p className="font-display text-xl font-bold">4.8%</p>
                <p className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600">
                  <ArrowDownRight className="h-3 w-3" /> −0.6% {t("adminReports.retention.vsLastMonth")}
                </p>
              </div>
              <div className="rounded-xl bg-surface p-3">
                <p className="text-[10px] text-muted-foreground">{t("adminReports.retention.repeat")}</p>
                <p className="font-display text-xl font-bold">58.2%</p>
                <p className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600">
                  <ArrowUpRight className="h-3 w-3" /> +5.4% {t("adminReports.retention.vsLastMonth")}
                </p>
              </div>
              <div className="rounded-xl bg-surface p-3">
                <p className="text-[10px] text-muted-foreground">{t("adminReports.retention.sessions")}</p>
                <p className="font-display text-xl font-bold">3.2x</p>
                <p className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600">
                  <ArrowUpRight className="h-3 w-3" /> +0.4x {t("adminReports.retention.vsLastMonth")}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Top performing spaces */}
      <Card className="mt-6 rounded-2xl border-border/60 shadow-soft">
        <div className="flex items-center justify-between p-5">
          <div>
            <p className="text-sm font-semibold">{t("adminReports.top.title")}</p>
            <p className="text-xs text-muted-foreground">{t("adminReports.top.subtitle")}</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-y border-border/60 bg-surface text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:bg-surface">
                <TableHead className="px-5 py-2.5">#</TableHead>
                <TableHead className="px-5 py-2.5">{t("adminReports.top.space")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("adminReports.top.category")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("adminReports.top.occupancy")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("adminReports.top.bookings")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("adminReports.top.revenue")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("adminReports.top.trend")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topSpaces.map((s) => (
                <TableRow
                  key={s.rank}
                  className="border-b border-border/40 transition-colors last:border-b-0 hover:bg-surface/50"
                >
                  <TableCell className="px-5 py-3 font-display text-sm font-semibold text-muted-foreground">
                    {s.rank}
                  </TableCell>
                  <TableCell className="px-5 py-3">
                    <p className="font-semibold">{s.name}</p>
                    <p className="text-xs text-muted-foreground">{s.location}</p>
                  </TableCell>
                  <TableCell className="px-5 py-3 text-sm text-muted-foreground">
                    {t(`adminReports.spaceCategories.${s.category}`, s.category)}
                  </TableCell>
                  <TableCell className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-[#5958F3]"
                          style={{ width: `${s.occupancy}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium">{s.occupancy}%</span>
                    </div>
                  </TableCell>
                  <TableCell className="px-5 py-3 font-semibold">{s.bookings}</TableCell>
                  <TableCell className="px-5 py-3 font-semibold">{s.revenue}</TableCell>
                  <TableCell className="px-5 py-3">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 text-xs font-semibold",
                        s.delta.startsWith("↑") ? "text-emerald-600" : "text-rose-600",
                      )}
                    >
                      {s.delta.startsWith("↑") ? (
                        <ArrowUpRight className="h-3 w-3" />
                      ) : (
                        <ArrowDownRight className="h-3 w-3" />
                      )}
                      {s.delta}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Scheduled reports */}
      <Card className="mt-6 rounded-2xl border-border/60 shadow-soft">
        <div className="flex items-center justify-between p-5">
          <div>
            <p className="text-sm font-semibold">{t("adminReports.scheduled.title")}</p>
            <p className="text-xs text-muted-foreground">{t("adminReports.scheduled.subtitle")}</p>
          </div>
          <Button
            className="h-10 gap-2 rounded-xl bg-[#5958F3] shadow-soft hover:bg-[#5958F3]/90"
            onClick={handleNewSchedule}
          >
            <Plus className="h-4 w-4" />
            {t("adminReports.scheduled.new")}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-y border-border/60 bg-surface text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:bg-surface">
                <TableHead className="px-5 py-2.5">{t("adminReports.scheduled.name")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("adminReports.scheduled.type")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("adminReports.scheduled.frequency")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("adminReports.scheduled.recipients")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("adminReports.scheduled.lastRun")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("adminReports.scheduled.nextRun")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("adminReports.scheduled.status")}</TableHead>
                <TableHead className="px-5 py-2.5 text-end">{t("adminReports.scheduled.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="px-5 py-10 text-center text-sm text-muted-foreground"
                  >
                    {t("adminReports.scheduled.empty")}
                  </TableCell>
                </TableRow>
              )}
              {reports.map((r) => (
                <TableRow
                  key={r.id}
                  className="border-b border-border/40 transition-colors last:border-b-0 hover:bg-surface/50"
                >
                  <TableCell className="px-5 py-3 font-semibold">{r.name}</TableCell>
                  <TableCell className="px-5 py-3 text-sm text-muted-foreground">
                    {t(`adminReports.types.${r.type}`, r.type)}
                  </TableCell>
                  <TableCell className="px-5 py-3 text-sm">
                    {t(`adminReports.frequencies.${r.frequency}`, r.frequency)}
                  </TableCell>
                  <TableCell className="px-5 py-3 text-sm text-muted-foreground">
                    {r.recipients}
                  </TableCell>
                  <TableCell className="px-5 py-3 text-sm text-muted-foreground">
                    {r.lastRun}
                  </TableCell>
                  <TableCell className="px-5 py-3 text-sm text-muted-foreground">
                    {r.nextRun}
                  </TableCell>
                  <TableCell className="px-5 py-3">
                    <Badge
                      variant="secondary"
                      className={cn(
                        "rounded-full text-[11px] font-semibold",
                        r.status === "Active"
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
                          : "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
                      )}
                    >
                      {r.status === "Active"
                        ? t("adminReports.scheduled.active")
                        : t("adminReports.scheduled.paused")}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-5 py-3 text-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onClick={() => toast.success(t("adminReports.toasts.running", { name: r.name }))}>
                          <FileText className="me-2 h-4 w-4" />
                          {t("adminReports.scheduled.runNow")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toast.info(t("adminReports.toasts.editor"))}>
                          <Pencil className="me-2 h-4 w-4" />
                          {t("adminReports.scheduled.edit")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleReport(r.id)}>
                          {r.status === "Active" ? (
                            <>
                              <Pause className="me-2 h-4 w-4" />
                              {t("adminReports.scheduled.pause")}
                            </>
                          ) : (
                            <>
                              <Play className="me-2 h-4 w-4" />
                              {t("adminReports.scheduled.activate")}
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-rose-600 focus:text-rose-600"
                          onClick={() => deleteReport(r.id)}
                        >
                          <Trash2 className="me-2 h-4 w-4" />
                          {t("adminReports.scheduled.delete")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </AdminShell>
  );
}
